import { Link, useLocation } from '@tanstack/react-router';
import { segmentedItemClass, segmentedListClass } from '@buildbee/ui';

/** Top-level sections of the platform console. */
export function PlatformNav() {
  const path = useLocation({ select: (l) => l.pathname });
  const section = path.startsWith('/admin/users') ? 'users' : path.startsWith('/admin/activity') ? 'activity' : 'companies';
  return (
    <nav aria-label="Platform admin" className="mb-6">
      <div className={segmentedListClass}>
        <Link to="/admin" className={segmentedItemClass(section === 'companies')} aria-current={section === 'companies' ? 'page' : undefined}>
          Companies
        </Link>
        <Link to="/admin/users" className={segmentedItemClass(section === 'users')} aria-current={section === 'users' ? 'page' : undefined}>
          People
        </Link>
        <Link to="/admin/activity" className={segmentedItemClass(section === 'activity')} aria-current={section === 'activity' ? 'page' : undefined}>
          Activity
        </Link>
      </div>
    </nav>
  );
}

export type CompanyTab = 'overview' | 'team' | 'templates' | 'suppliers';

/** Sections of one company's support page. */
export function CompanyTabs({ workspaceId, current }: { workspaceId: string; current: CompanyTab }) {
  const item = (tab: CompanyTab) => ({ className: segmentedItemClass(current === tab), 'aria-current': current === tab ? ('page' as const) : undefined });
  return (
    <nav aria-label="Company" className="flex flex-wrap items-center gap-3">
      <div className={segmentedListClass}>
        <Link to="/admin/workspaces/$workspaceId" params={{ workspaceId }} {...item('overview')}>
          Overview
        </Link>
        <Link to="/admin/workspaces/$workspaceId/team" params={{ workspaceId }} {...item('team')}>
          Team
        </Link>
        <Link to="/admin/workspaces/$workspaceId/templates" params={{ workspaceId }} {...item('templates')}>
          Templates
        </Link>
        <Link to="/admin/workspaces/$workspaceId/suppliers" params={{ workspaceId }} {...item('suppliers')}>
          Suppliers
        </Link>
      </div>
    </nav>
  );
}

/** "← All companies" plus the company tabs, shown on every company page. */
export function CompanyHeader({ workspaceId, current, name }: { workspaceId: string; current: CompanyTab; name?: string }) {
  return (
    <div className="space-y-3">
      <Link to="/admin" className="text-sm text-muted-foreground">
        ← All companies
      </Link>
      {name ? <h2 className="text-[19px] font-semibold tracking-[-0.02em]">{name}</h2> : null}
      <CompanyTabs workspaceId={workspaceId} current={current} />
    </div>
  );
}

/** Plain-language names for platform audit actions. */
export const ACTION_LABELS: Record<string, string> = {
  'platform.user_password_set': 'Set a password',
  'platform.user_password_reset_sent': 'Sent a password reset email',
  'platform.user_sessions_revoked': 'Signed out everywhere',
  'platform.user_locked': 'Locked account',
  'platform.user_unlocked': 'Unlocked account',
  'platform.user_email_verified_changed': 'Changed email verification',
  'platform.super_admin_granted': 'Made super admin',
  'platform.super_admin_removed': 'Removed super admin',
  'platform.member_added': 'Added to company',
  'platform.member_invited': 'Invited to company',
  'platform.member_role_changed': 'Changed role',
  'platform.member_status_changed': 'Changed member status',
  'platform.member_removed': 'Removed from company',
  'platform.invitation_revoked': 'Revoked invitation',
  'platform.project_template_created': 'Added project template',
  'platform.project_template_updated': 'Edited project template',
  'platform.project_template_archived': 'Archived project template',
  'platform.estimate_template_created': 'Added estimate template',
  'platform.estimate_template_updated': 'Edited estimate template',
  'platform.estimate_template_archived': 'Archived estimate template',
  'platform.document_template_created': 'Added proposal/email template',
  'platform.document_template_updated': 'Edited proposal/email template',
  'platform.document_template_archived': 'Archived proposal/email template',
  'platform.templates_installed': 'Added library templates',
  'platform.templates_copied': 'Copied templates',
  'platform.supplier_created': 'Added supplier',
  'platform.suppliers_imported': 'Imported suppliers',
  'platform.workspace_created': 'Created company',
  'platform.company_code_changed': 'Changed company code',
  'platform.plan_changed': 'Changed plan',
  'platform.trial_extended': 'Extended trial',
  'platform.workspace_suspended': 'Suspended company',
  'platform.workspace_unsuspended': 'Lifted suspension',
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/^platform\./, '').replace(/_/g, ' ');
}

/** One short line about what an action touched, from its metadata. */
export function actionDetail(e: { metadata: Record<string, unknown>; reason: string | null }): string {
  const m = e.metadata ?? {};
  const parts: string[] = [];
  for (const key of ['email', 'name', 'workspaceName'] as const) {
    if (typeof m[key] === 'string') parts.push(m[key] as string);
  }
  if (typeof m.role === 'string') parts.push(`as ${m.role}`);
  if (m.role && typeof m.role === 'object') {
    const r = m.role as { from?: string; to?: string };
    parts.push(`${r.from ?? '?'} → ${r.to ?? '?'}`);
  }
  if (typeof m.created === 'number') parts.push(`${m.created} added, ${String(m.skipped ?? 0)} skipped, ${String(m.failed ?? 0)} with errors`);
  if (e.reason) parts.push(`“${e.reason}”`);
  return parts.join(' · ');
}
